import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Eye, Loader2, Plus, RotateCcw, Sparkles, X } from "lucide-react";
import { useToast } from "@/shared/ui";
import { listModels, getModel, type VaultModel } from "../../features/vault/vaultApi";
import {
  cleanLabel,
  defaultIndexOf,
  patchOption,
  setDefaultOption,
  setGroupLabel,
  withSlotGroup,
  type ConfiguratorVariant,
} from "../../features/vault/configurator";
import { libraryItemId } from "../../features/vault/applyOption";
import { optionFill } from "../../ui/configurator/swatch";
import { OptionPicker } from "../../ui/editor/configurator/OptionPicker";
import { useConfiguratorEditing, type LiveSlot } from "../../ui/editor/configurator/useConfiguratorEditing";
import { partName } from "../../ui/editor/basic/rail/usePartScope";

const CURRENCIES = ["USD", "EUR", "GBP", "INR", "AUD", "CAD"];

/**
 * The customer configurator's details drawer (top bar › "Customer
 * configurator…", or the price tag on the canvas bar): per part, the name a
 * customer sees, the choices with their price add, and which one it opens on;
 * plus the base price, currency and whole-model variants.
 *
 * It edits the SAME draft as the canvas bar (`ui/editor/configurator/draft.ts`)
 * — add a choice here and it appears on the bar, and the other way round —
 * and choices come from the material library through the same picker. Trying
 * a choice is an ordinary studio edit: it shows on the model as one Undo step.
 *
 * Mounted by the studio inside the editor's providers (`EditorShell`'s
 * `overlay`), which the library grid needs.
 */
export default function ConfiguratorBuilder({ modelId, onClose }: { modelId: string; onClose: () => void }) {
  const { state, draft, slots, busy, addFromLibrary, choose, remove } = useConfiguratorEditing();
  const [models, setModels] = useState<VaultModel[]>([]); // ready models the designer can add
  const [baseVariant, setBaseVariant] = useState<ConfiguratorVariant | null>(null);
  /** The part whose library picker is open, by slot index. */
  const [picking, setPicking] = useState<number | null>(null);
  const { toast } = useToast();

  useEffect(() => draft.bindModel(modelId), [draft, modelId]);
  // Opening the drawer shows the bar too: the drawer edits what it shows.
  useEffect(() => draft.setOn(true), [draft]);

  useEffect(() => {
    let alive = true;
    // The variant picker is a nicety; a hiccup on this workspace-wide list
    // must not take the drawer down with it. Variants can live in any folder.
    // ponytail: recent caps at 50; add a dedicated all-ready endpoint if a workspace outgrows that.
    void listModels(null, { recent: true })
      .then((all) => {
        if (alive) setModels(all.filter((m) => m.status === "ready" && m._id !== modelId));
      })
      .catch(() => {
        /* variant picker just stays empty */
      });
    void getModel(modelId)
      .then((m) => {
        if (alive) setBaseVariant({ id: modelId, label: m.name, modelId, thumbUrl: m.posterUrl ?? undefined });
      })
      .catch(() => {
        /* the base row falls back to the id */
      });
    return () => {
      alive = false;
    };
  }, [modelId]);

  const def = state.def;
  const base = baseVariant ?? { id: modelId, label: "This model", modelId };
  // Index 0 is always the model this studio is open on.
  const variants = def.variants?.length ? def.variants : [base];
  const setVariants = (next: ConfiguratorVariant[]) =>
    // Only persisted when there is a real choice (>1), so single-model defs stay clean.
    draft.edit("variants", (d) => ({ ...d, variants: next.length > 1 ? next : undefined }));

  const basePrice = def.basePrice == null ? "" : String(def.basePrice);
  const setBasePrice = (raw: string) => {
    const n = raw.trim() === "" ? undefined : Number(raw);
    draft.edit("pricing", (d) => ({
      ...d,
      basePrice: n != null && !Number.isNaN(n) ? n : undefined,
      currency: d.currency ?? "USD",
    }));
  };
  const setCurrency = (currency: string) => draft.edit("pricing", (d) => ({ ...d, currency }));

  /** The def group for a slot, created on first edit. */
  const groupIdFor = (slot: LiveSlot): string => {
    if (slot.config) return slot.config.id;
    let id = "";
    draft.edit("groups", (d) => {
      const r = withSlotGroup(
        d,
        { label: slot.group.label, kind: slot.group.kind, index: slot.index, loadKind: slot.loadKind },
        slots.map((s) => s.group),
      );
      id = r.groupId;
      return r.def;
    });
    return id;
  };

  const save = async () => {
    try {
      await draft.save();
      toast({ tone: "success", title: "Configurator saved" });
    } catch {
      toast({
        tone: "error",
        title: "Could not save the configurator",
        description: "Check your connection and permissions, then try again.",
      });
    }
  };

  const pickingSlot = picking != null ? slots.find((s) => s.index === picking) : undefined;
  const dirty = draft.dirty;

  return (
    <motion.div
      initial={{ x: 360, opacity: 0 }}
      animate={{ x: 0, opacity: 1 }}
      exit={{ x: 360, opacity: 0 }}
      transition={{ type: "spring", stiffness: 320, damping: 34 }}
      className="vault-surface absolute bottom-0 right-0 top-0 z-[100] flex w-[340px] flex-col border-l border-ink-100 bg-white shadow-pop"
      data-config-builder
    >
      <div className="flex h-12 shrink-0 items-center justify-between border-b border-ink-100 px-4">
        <div className="flex items-center gap-2">
          {pickingSlot ? (
            <button
              onClick={() => setPicking(null)}
              className="-ml-1 rounded-md p-1 text-ink-500 hover:bg-ink-50 hover:text-ink-900"
              aria-label="Back to the configurator"
            >
              <ArrowLeft size={16} />
            </button>
          ) : (
            <Sparkles size={15} className="text-gold-500" />
          )}
          <span className="text-[13px] font-semibold text-ink-900">
            {pickingSlot ? `Add to ${partName(pickingSlot.group, pickingSlot.index, pickingSlot.kind)}` : "Configurator"}
          </span>
        </div>
        <button onClick={onClose} className="rounded-md p-1 text-ink-400 hover:bg-ink-50 hover:text-ink-900" aria-label="Close">
          <X size={16} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {pickingSlot ? (
          <div className="editor-theme">
            <p className="mb-3 text-[12px] leading-relaxed text-ink-500">
              Each material you pick becomes a choice customers can switch to.
            </p>
            <OptionPicker
              key={pickingSlot.group.id}
              kind={pickingSlot.kind}
              activeId={(() => {
                const c = pickingSlot.config;
                const o = c?.options[defaultIndexOf(c)];
                return o ? libraryItemId(o.id) : null;
              })()}
              onPick={(item) => addFromLibrary(pickingSlot, item)}
            />
          </div>
        ) : state.load === "loading" ? (
          <div className="flex h-40 items-center justify-center text-ink-400">
            <Loader2 size={18} className="animate-spin" />
          </div>
        ) : state.load === "error" ? (
          <div className="flex flex-col items-start gap-3">
            <p className="text-[13px] leading-relaxed text-ink-500">
              Could not read this model's saved configurator. Nothing has been changed.
            </p>
            <button
              onClick={() => draft.reload()}
              className="flex items-center gap-1.5 rounded-lg border border-ink-200 px-3 py-1.5 text-[12px] font-medium text-ink-800 hover:border-ink-300"
            >
              <RotateCcw size={13} /> Try again
            </button>
          </div>
        ) : slots.length === 0 ? (
          <p className="text-[13px] leading-relaxed text-ink-500">
            This model has no parts to configure yet. Once it has loaded, every metal and stone part
            appears here.
          </p>
        ) : (
          <div className="space-y-6">
            <p className="text-[12px] leading-relaxed text-ink-500">
              Add the materials customers can switch between, per part, with an optional price add.
              Click a swatch to show it on the model — it becomes the one the part opens on, and
              Undo puts the material back.
            </p>

            {/* Base price */}
            <div className="rounded-xl border border-ink-100 bg-ink-50/50 p-3">
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                Base price
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  inputMode="decimal"
                  value={basePrice}
                  onChange={(e) => setBasePrice(e.target.value)}
                  placeholder="e.g. 1200"
                  className="flex-1 rounded-lg border border-ink-200 px-3 py-1.5 text-[13px] text-ink-900 focus:border-gold-400 focus:outline-none"
                />
                <select
                  value={def.currency ?? "USD"}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="rounded-lg border border-ink-200 px-2 py-1.5 text-[13px] text-ink-800 focus:border-gold-400 focus:outline-none"
                >
                  {CURRENCIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
              <p className="mt-1.5 text-[11px] text-ink-400">Leave blank to hide pricing in the embed.</p>
            </div>

            {/* Model variants */}
            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                Model variants
              </label>
              <p className="mb-2.5 text-[11px] leading-relaxed text-ink-400">
                Let customers switch between whole models. The current model is the base; add other
                ready models as options.
              </p>
              <div className="flex flex-col gap-2">
                {variants.map((v, vi) => (
                  <div key={v.id} className="flex items-center gap-2">
                    {v.thumbUrl ? (
                      <img src={v.thumbUrl} alt="" className="h-8 w-8 shrink-0 rounded-lg border border-ink-200 object-cover" />
                    ) : (
                      <span className="h-8 w-8 shrink-0 rounded-lg border border-ink-200 bg-ink-50" />
                    )}
                    <input
                      value={v.label}
                      onChange={(e) => setVariants(variants.map((x) => (x.id === v.id ? { ...x, label: e.target.value } : x)))}
                      className="min-w-0 flex-1 rounded-lg border border-ink-200 px-3 py-1.5 text-[13px] text-ink-900 focus:border-gold-400 focus:outline-none"
                    />
                    {vi === 0 ? (
                      <span className="shrink-0 rounded-md bg-ink-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-ink-500">
                        Base
                      </span>
                    ) : (
                      <button
                        onClick={() => setVariants(variants.filter((x) => x.id !== v.id))}
                        className="shrink-0 rounded-md p-1.5 text-ink-400 hover:bg-ink-50 hover:text-ink-900"
                        title="Remove variant"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {(() => {
                const addable = models.filter((m) => !variants.some((v) => v.modelId === m._id));
                return (
                  <select
                    value=""
                    disabled={addable.length === 0}
                    onChange={(e) => {
                      const m = addable.find((x) => x._id === e.target.value);
                      if (m) setVariants([...variants, { id: m._id, label: m.name, modelId: m._id, thumbUrl: m.posterUrl ?? undefined }]);
                    }}
                    className="mt-2 w-full rounded-lg border border-ink-200 px-3 py-1.5 text-[13px] text-ink-800 focus:border-gold-400 focus:outline-none disabled:opacity-50"
                  >
                    <option value="" disabled>
                      {addable.length === 0 ? "No other ready models" : "Add a model…"}
                    </option>
                    {addable.map((m) => (
                      <option key={m._id} value={m._id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                );
              })()}
            </div>

            {slots.map((slot) => {
              const g = slot.config;
              const options = g?.options ?? [];
              const opens = g ? defaultIndexOf(g) : -1;
              // The slot's own kind (its name, else as it loaded), not what it is wearing.
              const kind = slot.kind;
              return (
                // Keyed by ordinal, not label: a GLB can hold two same-named groups.
                <div key={slot.index} data-config-builder-part={slot.index}>
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                    {slot.group.label} — shown as
                  </label>
                  <input
                    value={g?.label ?? cleanLabel(slot.group.label, kind)}
                    onChange={(e) => {
                      const id = groupIdFor(slot);
                      draft.edit("groups", (d) => setGroupLabel(d, id, e.target.value));
                    }}
                    className="mb-2.5 w-full rounded-lg border border-ink-200 px-3 py-1.5 text-[13px] text-ink-900 focus:border-gold-400 focus:outline-none"
                  />
                  <div className="flex flex-col gap-2">
                    {options.map((o, i) => (
                      <div key={o.id} className="flex items-center gap-2">
                        <button
                          onClick={() => void choose(slot, o.id)}
                          title={`Show ${o.name} on the model`}
                          aria-pressed={i === opens}
                          className={`group flex min-w-0 flex-1 items-center gap-2 rounded-lg border px-2.5 py-2 text-left text-[12px] transition-colors ${
                            i === opens ? "border-gold-400 bg-gold-50" : "border-ink-200 bg-white hover:border-ink-300"
                          }`}
                        >
                          <span
                            className="h-5 w-5 shrink-0 rounded-full border border-black/10 bg-cover bg-center"
                            style={optionFill(o)}
                          />
                          <span className="flex-1 truncate text-ink-800">{o.name}</span>
                          {busy === o.id ? (
                            <Loader2 size={12} className="shrink-0 animate-spin text-ink-400" />
                          ) : (
                            <Eye size={12} className="shrink-0 text-ink-300 group-hover:text-ink-600" />
                          )}
                        </button>
                        <div className={`flex items-center rounded-lg border ${basePrice === "" ? "border-ink-100 opacity-40" : "border-ink-200"}`}>
                          <span className="pl-2 text-[11px] text-ink-400">+</span>
                          <input
                            type="number"
                            inputMode="decimal"
                            disabled={basePrice === ""}
                            value={o.priceDelta || ""}
                            onChange={(e) =>
                              draft.edit("groups", (d) => patchOption(d, g!.id, o.id, { priceDelta: Number(e.target.value) || 0 }))
                            }
                            placeholder="0"
                            aria-label={`Price add for ${o.name}`}
                            className="w-14 bg-transparent px-1 py-1.5 text-[12px] text-ink-800 outline-none disabled:cursor-not-allowed"
                          />
                        </div>
                        <button
                          onClick={() => remove(slot, o.id)}
                          className="shrink-0 rounded-md p-1.5 text-ink-400 hover:bg-ink-50 hover:text-ink-900"
                          title={`Remove ${o.name}`}
                          aria-label={`Remove ${o.name}`}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={() => setPicking(slot.index)}
                      className="flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-ink-300 px-3 py-2 text-[12px] font-medium text-ink-700 hover:border-ink-400 hover:text-ink-900"
                      data-config-builder-add={slot.index}
                    >
                      <Plus size={13} /> Add from the library
                    </button>
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-ink-400">
                      Opens on
                    </span>
                    <select
                      value={options[opens]?.id ?? ""}
                      disabled={options.length === 0}
                      onChange={(e) => {
                        const idx = options.findIndex((o) => o.id === e.target.value);
                        if (g && idx >= 0) draft.edit("groups", (d) => setDefaultOption(d, g.id, idx));
                      }}
                      className="min-w-0 flex-1 rounded-lg border border-ink-200 px-2 py-1.5 text-[12px] text-ink-800 focus:border-gold-400 focus:outline-none disabled:opacity-50"
                    >
                      {options.length === 0 ? (
                        <option value="">No choices yet — this part is hidden</option>
                      ) : (
                        options.map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-ink-100 p-3">
        <button
          onClick={() => void save()}
          disabled={state.saving || state.load === "loading" || slots.length === 0}
          className="btn-press flex w-full items-center justify-center gap-2 rounded-xl bg-ink-900 py-2.5 text-[13px] font-semibold text-white hover:bg-ink-800 disabled:opacity-50"
          data-config-builder-save
        >
          {state.saving ? <Loader2 size={14} className="animate-spin" /> : null}
          {state.saving ? "Saving…" : dirty ? "Save configurator" : "Saved ✓"}
        </button>
      </div>
    </motion.div>
  );
}
